import React, { useMemo } from "react";
import { useT, Namespace } from "@racoon/i18n";
import { DataTable, Column } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useTableSettings } from "~/shared/hooks/useTableSettings";

type CustomTruckType = ConnectProto.CustomTruckType;

interface TruckTypesTableProps {
  truckTypes: CustomTruckType[];
  isLoading: boolean;
  selectedType?: CustomTruckType | null;
  onRowSelect?: (type: CustomTruckType | null) => void;
}

export const TruckTypesTable: React.FC<TruckTypesTableProps> = ({
  truckTypes,
  isLoading,
  selectedType,
  onRowSelect,
}) => {
  const t = useT();
  const { selectedCompanyId } = useSelectedCompany();
  const { columnConfig, handleColumnConfigChange } = useTableSettings(
    "truck_types",
    selectedCompanyId || undefined,
  );

  const columns = useMemo<Column<CustomTruckType>[]>(() => {
    return [
      {
        id: "index",
        header: "№",
        accessor: (_r, i) => i + 1,
        width: 64,
      },
      {
        id: "name",
        header: t(Namespace.Settings, "truckType.table.name"),
        accessor: (row) => row.name,
        sortable: true,
        width: 300,
      },
      {
        id: "truckCount",
        header: t(Namespace.Settings, "truckType.table.truckCount"),
        accessor: (row) => row.truckCount,
        sortable: true,
        width: 200,
      },
      {
        // Types are shared across the holding: the company that made each one
        id: "owner",
        header: t(Namespace.Settings, "truckType.table.owner"),
        accessor: (row) => row.ownerOrganization?.name ?? "",
        width: 220,
      },
    ];
  }, [t]);

  if (truckTypes.length === 0 && !isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
        <p className="text-gray-500">{t(Namespace.Settings, "truckType.table.empty")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 rounded-lg">
      <DataTable
        className="h-full"
        columns={columns}
        data={truckTypes}
        loading={isLoading}
        rowKey={(row) => row.id.toString()}
        selectedRowKeys={selectedType ? [selectedType.id.toString()] : []}
        onRowClick={
          onRowSelect
            ? (row) => {
                if (selectedType?.id === row.id) {
                  onRowSelect(null);
                } else {
                  onRowSelect(row);
                }
              }
            : undefined
        }
        columnConfig={columnConfig}
        onColumnConfigChange={handleColumnConfigChange}
        namespace={Namespace.Settings}
      />
    </div>
  );
};
